import React, { useEffect, useState } from 'react';
import { CheckCircle2, ChevronLeft, Loader2, Send } from 'lucide-react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import axios from '../../utils/axiosConfig';

const EVENT_TYPES = ['Cumpleaños', '15 años', 'Casamiento', 'Bautismo', 'Evento corporativo', 'Fiesta privada', 'Otro'];
const inputClass = 'h-11 w-full border border-white/10 bg-black/20 px-3 text-sm text-white outline-none transition-colors focus:border-primary-orange';

export default function QuoteEventForm() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const [salon, setSalon] = useState(null);
  const [loadingSalon, setLoadingSalon] = useState(true);
  const [checking, setChecking] = useState(false);
  const [availability, setAvailability] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    requestedDate: searchParams.get('date') || '',
    startTime: '',
    endTime: '',
    eventType: '',
    guestCount: '',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    message: '',
  });

  useEffect(() => {
    let active = true;
    axios.get(`/api/clients/${slug}`)
      .then(({ data }) => { if (active) setSalon(data); })
      .catch(() => { if (active) setError('No pudimos identificar el salón.'); })
      .finally(() => { if (active) setLoadingSalon(false); });
    return () => { active = false; };
  }, [slug]);

  useEffect(() => {
    if (!form.requestedDate || salon?.organizationType !== 'venue') {
      setAvailability(null);
      return undefined;
    }
    let active = true;
    const timer = window.setTimeout(async () => {
      setChecking(true);
      try {
        const { data } = await axios.get(`/${slug}/api/quotes/availability`, { params: { from: form.requestedDate, to: form.requestedDate } });
        if (active) setAvailability(!data.occupiedDates?.includes(form.requestedDate));
      } catch {
        if (active) setAvailability(null);
      } finally {
        if (active) setChecking(false);
      }
    }, 250);
    return () => { active = false; window.clearTimeout(timer); };
  }, [form.requestedDate, salon?.organizationType, slug]);

  const update = (field) => (event) => setForm((current) => ({ ...current, [field]: event.target.value }));

  const submit = async (event) => {
    event.preventDefault();
    setError('');
    if (availability === false) {
      setError('La fecha seleccionada ya está ocupada. Elegí otra fecha.');
      return;
    }
    try {
      setSaving(true);
      await axios.post(`/${slug}/api/quotes`, form);
      setSubmitted(true);
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'No se pudo enviar la consulta.');
    } finally {
      setSaving(false);
    }
  };

  if (loadingSalon) return <div className="flex min-h-[60vh] items-center justify-center text-primary-orange"><Loader2 className="animate-spin" size={30} /></div>;
  if (salon?.organizationType !== 'venue') return <div className="mx-auto max-w-xl py-20 text-center text-white/60">La consulta de disponibilidad está disponible únicamente para salones de fiestas.</div>;

  if (submitted) {
    return (
      <section className="mx-auto max-w-2xl py-16">
        <div className="border border-emerald-500/30 bg-emerald-500/10 p-8 text-center sm:p-12">
          <CheckCircle2 className="mx-auto text-emerald-400" size={44} />
          <h1 className="mt-6 text-3xl font-black text-white">Consulta enviada</h1>
          <p className="mx-auto mt-4 max-w-lg text-base leading-relaxed text-white/65">El equipo de {salon.nombre} recibió tu solicitud y se comunicará con vos para preparar el presupuesto.</p>
          <Link to={`/${slug}`} className="mt-8 inline-flex h-11 items-center gap-2 bg-primary-orange px-4 text-sm font-bold text-white hover:bg-orange-500"><ChevronLeft size={17} />Volver al salón</Link>
        </div>
      </section>
    );
  }

  return (
    <section className="mx-auto max-w-3xl py-8 sm:py-14">
      <Link to={`/${slug}`} className="inline-flex items-center gap-2 text-sm font-bold text-white/55 hover:text-white"><ChevronLeft size={17} />Volver al salón</Link>
      <div className="mt-8 border border-white/10 bg-[#111111] p-6 sm:p-10">
        <p className="text-[11px] font-black uppercase tracking-[2px] text-primary-orange">{salon.nombre}</p>
        <h1 className="mt-3 text-4xl font-black text-white sm:text-5xl">Solicitar presupuesto</h1>
        <p className="mt-4 max-w-xl text-sm leading-relaxed text-white/55">La fecha se confirma únicamente cuando el salón responda y registre la reserva.</p>

        <form onSubmit={submit} className="mt-9 grid gap-6 sm:grid-cols-2">
          <Field label="Fecha" required>
            <input type="date" value={form.requestedDate} onChange={update('requestedDate')} min={new Date().toISOString().slice(0, 10)} required className={inputClass} />
            {checking ? <p className="mt-2 text-xs font-bold text-white/45">Verificando fecha...</p> : null}
            {availability === true ? <p className="mt-2 text-xs font-bold text-emerald-400">Fecha disponible</p> : null}
            {availability === false ? <p className="mt-2 text-xs font-bold text-red-300">Fecha ocupada</p> : null}
          </Field>
          <Field label="Tipo de evento" required>
            <select value={form.eventType} onChange={update('eventType')} required className={inputClass}><option value="">Seleccioná una opción</option>{EVENT_TYPES.map((type) => <option key={type} value={type}>{type}</option>)}</select>
          </Field>
          <Field label="Horario de inicio">
            <input type="time" value={form.startTime} onChange={update('startTime')} className={inputClass} />
          </Field>
          <Field label="Horario de cierre">
            <input type="time" value={form.endTime} onChange={update('endTime')} className={inputClass} />
          </Field>
          <Field label="Cantidad estimada de invitados">
            <input type="number" min="1" value={form.guestCount} onChange={update('guestCount')} className={inputClass} placeholder="Ej. 80" />
          </Field>
          <Field label="Tu nombre" required>
            <input value={form.contactName} onChange={update('contactName')} required className={inputClass} placeholder="Nombre y apellido" />
          </Field>
          <Field label="Email">
            <input type="email" value={form.contactEmail} onChange={update('contactEmail')} className={inputClass} placeholder="nombre@email.com" />
          </Field>
          <Field label="WhatsApp o teléfono">
            <input type="tel" value={form.contactPhone} onChange={update('contactPhone')} className={inputClass} placeholder="351 ..." />
          </Field>
          <div className="sm:col-span-2">
            <Field label="Contanos qué necesitás">
              <textarea value={form.message} onChange={update('message')} maxLength={1000} rows={5} className={`${inputClass} h-auto py-3 resize-none`} placeholder="Comida, decoración, tipo de celebración u otros detalles" />
            </Field>
          </div>
          {error ? <p className="sm:col-span-2 text-sm font-bold text-red-300">{error}</p> : null}
          <button type="submit" disabled={saving || checking || availability === false} className="sm:col-span-2 inline-flex h-12 items-center justify-center gap-2 bg-primary-orange px-5 text-sm font-black text-white transition-colors hover:bg-orange-500 disabled:cursor-not-allowed disabled:opacity-50"><Send size={18} />{saving ? 'Enviando consulta' : 'Enviar consulta'}</button>
        </form>
      </div>
    </section>
  );
}

function Field({ label, required, children }) {
  return <label className="block"><span className="mb-2 block text-[10px] font-black uppercase tracking-[1.5px] text-white/45">{label}{required ? ' *' : ''}</span>{children}</label>;
}
